/**
 * Ticket Detail — full conversation view + reply + one-click escalate.
 *
 * Route: /workspace/support/help/:ticket_id
 */
import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import {
  ArrowLeft, Sparkles, Loader2, ShieldAlert, Clock, TicketIcon, Send, AlertTriangle,
} from "lucide-react";

interface Ticket {
  ticket_id: string;
  subject: string;
  message: string;
  category: string;
  status: "open" | "in_progress" | "resolved";
  severity: "low" | "medium" | "high";
  sla_hours: number;
  sla_deadline?: string;
  ai_reply?: string;
  escalated?: boolean;
  created_at: string;
}

function hoursUntil(iso?: string): number {
  if (!iso) return 0;
  return Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 3600000));
}

export default function TicketDetail() {
  const { ticket_id } = useParams<{ ticket_id: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [reply, setReply] = React.useState("");

  const ticketQ = useQuery({
    queryKey: ["support-ticket", ticket_id],
    enabled: !!ticket_id,
    queryFn: async () => (await api.get(`/support/tickets/${ticket_id}`)).data as (Ticket & { replies?: any[] }),
  });
  const ticket = ticketQ.data;

  const escalate = useMutation({
    mutationFn: async () => (await api.post(`/support/tickets/${ticket_id}/escalate`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["support-ticket", ticket_id] });
      qc.invalidateQueries({ queryKey: ["support-tickets"] });
    },
  });

  const sendReply = useMutation({
    mutationFn: async () => (await api.post(`/support/tickets/${ticket_id}/reply`, { text: reply.trim() })).data,
    onSuccess: () => {
      setReply("");
      qc.invalidateQueries({ queryKey: ["support-ticket", ticket_id] });
      qc.invalidateQueries({ queryKey: ["support-tickets"] });
    },
  });

  if (ticketQ.isLoading) {
    return <DashboardLayout title="Ticket" subtitle="Loading…" seoNoindex><div className="sg-skeleton h-40"/></DashboardLayout>;
  }
  if (!ticket) {
    return (
      <DashboardLayout title="Ticket not found" subtitle="This ticket may have been closed or belongs to a different workspace." seoNoindex>
        <div className="max-w-lg mx-auto py-12 text-center">
          <ShieldAlert className="h-10 w-10 mx-auto text-rose-500 mb-2"/>
          <div className="text-sm text-[color:var(--sg-muted)]">Ticket <code>{ticket_id}</code> could not be found.</div>
          <Link to="/workspace/support/help" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[color:var(--sb-saffron,#F97316)] hover:underline">
            <ArrowLeft className="h-4 w-4"/> Back to Help Center
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const slaLeft = hoursUntil(ticket.sla_deadline);

  return (
    <DashboardLayout
      title={ticket.subject}
      subtitle={`Ticket ${ticket.ticket_id} · ${ticket.category}`}
      seoNoindex
    >
      <div className="mb-4">
        <Link to="/workspace/support/help" data-testid="ticket-back" className="inline-flex items-center gap-1 text-sm text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)]">
          <ArrowLeft className="h-4 w-4"/> Back to Help Center
        </Link>
      </div>

      {/* SLA banner */}
      <div className={`rounded-2xl border p-4 mb-6 flex items-start justify-between gap-3 ${
        ticket.escalated
          ? "border-rose-400/50 bg-rose-500/5"
          : slaLeft <= 1
            ? "border-amber-400/50 bg-amber-500/5"
            : "border-[color:var(--sg-border)] bg-[color:var(--sg-card)]"
      }`} data-testid="ticket-sla-banner">
        <div className="flex items-start gap-3">
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${
            ticket.escalated ? "bg-rose-500 text-white" : "bg-[color:var(--sg-muted-bg)] text-[color:var(--sg-fg)]"
          }`}>
            {ticket.escalated ? <AlertTriangle className="h-5 w-5" strokeWidth={2.5}/> : <Clock className="h-5 w-5" strokeWidth={2.5}/>}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)] font-semibold">
              {ticket.escalated ? "Escalated" : `SLA ${ticket.sla_hours}h`}
            </div>
            <div className="font-serif-display text-lg font-bold text-[color:var(--sg-fg)] leading-tight">
              {ticket.escalated
                ? "Handed off to a human agent."
                : slaLeft > 0
                  ? `${slaLeft}h left until SLA breach.`
                  : "SLA breach imminent. Escalate?"}
            </div>
            <div className="text-xs text-[color:var(--sg-muted)] mt-0.5">
              Severity <b className="uppercase">{ticket.severity}</b> · Status <b className="capitalize">{ticket.status.replace("_"," ")}</b>
            </div>
          </div>
        </div>
        {!ticket.escalated && (
          <Button
            data-testid="ticket-escalate-btn"
            onClick={() => escalate.mutate()}
            disabled={escalate.isPending}
            className="bg-rose-500 hover:bg-rose-600 text-white h-10 font-semibold"
          >
            {escalate.isPending ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin"/> Escalating…</> : <><AlertTriangle className="h-4 w-4 mr-1.5"/> Escalate now</>}
          </Button>
        )}
      </div>

      {/* Conversation */}
      <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-5 mb-6 space-y-4" data-testid="ticket-conversation">
        {/* Original user message */}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)] font-semibold mb-1 flex items-center gap-1.5">
            <TicketIcon className="h-3 w-3"/> You · {new Date(ticket.created_at).toLocaleString()}
          </div>
          <div className="rounded-xl bg-[color:var(--sg-muted-bg)] px-4 py-3 text-sm text-[color:var(--sg-fg)] whitespace-pre-wrap">
            <div className="font-semibold mb-1">{ticket.subject}</div>
            {ticket.message}
          </div>
        </div>

        {/* AI first-reply */}
        {ticket.ai_reply && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--sb-saffron,#F97316)] font-semibold mb-1 flex items-center gap-1.5">
              <Sparkles className="h-3 w-3"/> AI first-reply · Claude Sonnet 4.5
            </div>
            <div className="rounded-xl border border-[color:var(--sg-border)] bg-white px-4 py-3 text-sm text-[color:var(--sg-fg)] whitespace-pre-wrap">
              {ticket.ai_reply}
            </div>
          </div>
        )}

        {/* User + support conversation thread */}
        {(ticket.replies || []).map((r: any) => (
          <div key={r.reply_id} data-testid={`ticket-reply-${r.reply_id}`}>
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)] font-semibold mb-1 flex items-center gap-1.5">
              <TicketIcon className="h-3 w-3"/> {r.author === "user" ? "You" : "Support"} · {new Date(r.created_at).toLocaleString()}
            </div>
            <div className="rounded-xl bg-[color:var(--sg-muted-bg)] px-4 py-3 text-sm text-[color:var(--sg-fg)] whitespace-pre-wrap">
              {r.text}
            </div>
          </div>
        ))}
      </div>

      {/* Reply back */}
      <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-5" data-testid="ticket-reply-form">
        <div className="text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)] font-semibold mb-2">Reply</div>
        <Textarea
          data-testid="ticket-reply-input"
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder="Follow-up details, extra context, screenshots — anything that helps our team resolve this faster."
          rows={4}
          className="bg-white"
        />
        <div className="mt-3 flex items-center justify-between">
          <span className="text-[11px] text-[color:var(--sg-muted)]">Replies extend the SLA by 2h automatically.</span>
          <Button
            data-testid="ticket-reply-submit"
            onClick={() => sendReply.mutate()}
            disabled={sendReply.isPending || !reply.trim()}
            className="bg-[color:var(--sb-saffron,#F97316)] text-white hover:bg-[color:var(--sb-saffron-hover,#EA580C)] h-10 font-semibold"
          >
            {sendReply.isPending
              ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin"/> Sending…</>
              : <><Send className="h-4 w-4 mr-1.5"/> Send reply</>}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
